import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import storeIndex from './store'
import i18n from './plugins/i18n'
import '@fortawesome/fontawesome-free/css/all.min.css'
import 'vue3-easy-data-table/dist/style.css'
import Vue3Toasity, { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';
import VueSweetalert2 from 'vue-sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { initDropdownHelper, closeActionDropdowns } from './utils/dropdownHelper'
import i18nName from './utils/i18nName'
import { brand } from './utils/brand'
import { useLicenseStore } from './stores/license.store'
import { registerPosDeviceInterceptor } from './services/posDevice'
import { initPrinterSettings } from './utils/printerSettings'
import { useShopStore } from './stores/shop.store'

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

// แนบ X-POS-Device-Token ให้ทุก request — บอก API ว่าคำสั่งนี้มาจากเครื่อง POS ตัวไหน
// (api/docs/vat-compliance.md D10) ไม่มี token ก็ไม่แนบ ระบบทำงานปกติ
registerPosDeviceInterceptor();

const app = createApp(App);
app.use(pinia);

// ค่าเครื่องพิมพ์ของเครื่องนี้ (docs/thermal-printer-setup.md §Device printer settings)
// เครื่องที่ยังไม่เคยตั้งแต่ Chrome จับคู่เครื่องพิมพ์ USB ไว้แล้ว = แคชเชียร์เดิมก่อนอัปเดต
// → ตั้งให้เองด้วยขนาดกระดาษของสาขาที่ shop store แคชไว้ตอนเข้าร้านครั้งล่าสุด
// ต้องอยู่หลัง app.use(pinia) เพราะอ่าน store
initPrinterSettings({ legacyPaperWidth: useShopStore().billPaperWidth });
app.use(router);
app.use(i18n);
app.use(i18nName);   // $ln(obj, field) — เลือกภาษาให้ชื่อที่มาคู่ name/name_en
app.use(VueSweetalert2, {
  scrollbarPadding: false,
  heightAuto: false
});
app.use(Vue3Toasity,{
  autoClose: 5000,
  pauseOnHover: true,
  rtl: false,
  closeOnClick: true,
  hideProgressBar: false,
  dangerouslyHTMLString: true
});
app.use({
  install() {
    window._ = require('lodash');
    window.$ = window.jQuery = require('jquery');
    // ตาข่ายรับ this.$t หลัง component ถูก unmount ไปแล้ว
    //
    // vue-i18n โหมด legacy แปะ $t ไว้บน instance ผ่าน mixin แล้ว `delete this.$t`
    // ทิ้งตอน unmounted — โค้ดแบบ `await api(); this.$toast.success(this.$t('...'))`
    // จึงพังด้วย "vm.$t is not a function" ถ้าระหว่างรอ await component ถูกถอดออก
    // (offcanvas ที่ปิดตัวเองหลัง submit, redirect, v-if ที่พลิกจาก state ใหม่)
    //
    // Vue หาคีย์ที่ขึ้นต้นด้วย $ โดยไม่ผ่าน accessCache และไล่มาที่ globalProperties
    // ต่อจาก ctx — พอ ctx.$t ถูกลบ ตัวนี้จึงรับช่วงให้เองโดยไม่ต้องแก้ทีละหน้า
    // ตอน mounted อยู่ยังใช้ $t ของ instance ตามเดิม ไม่ได้ทับของเดิม
    app.config.globalProperties.$t = (...args) => i18n.global.t(...args);
    app.config.globalProperties.$toast = toast;
    app.config.globalProperties.$storeIndex = storeIndex;
    // ชื่อร้านเป็นคู่ name/name_en เหมือนชื่อสินค้า → ใช้ $ln($brand) ได้ตรง ๆ (utils/brand.js)
    app.config.globalProperties.$brand = brand;
    app.config.globalProperties.$appName = process.env.VUE_APP_APP_NAME || 'SIVA';
    // แพ็กของร้านมีฟีเจอร์นี้ไหม — ใช้ซ่อนเมนู/ปุ่ม ($hasFeature('feature_inventory'))
    // ตัวบังคับจริงอยู่ที่ API (../silver-spoon-api/docs/plans-subscriptions.md §7) · อ่าน store ตอน render = reactive เอง
    app.config.globalProperties.$hasFeature = (slug) => useLicenseStore().hasFeature(slug);
  }
});
initDropdownHelper();


router.beforeEach((to, from, next) => {
  // เก็บกวาด action dropdown ที่ค้างอยู่ก่อนเปลี่ยนหน้า — เมนูถูกย้ายไปแขวนใต้
  // <body> ตอนเปิด (utils/dropdownHelper.js) ถ้า Vue ถอดหน้าเดิมทิ้งก่อนที่
  // Bootstrap จะปิดให้ เมนูจะลอยค้างตามไปหน้าถัดไป
  closeActionDropdowns();

  const appName = process.env.VUE_APP_APP_NAME || 'SIVA';
  const pageTitle = to.meta.title
    ? i18n.global.t(to.meta.title) + ' - ' + i18n.global.t(to.meta.suffix)
    : i18n.global.t('Back office');
  document.title = pageTitle + ' · ' + appName;
  next();
});

app.mount('#app');
